"use client"

import Image from "next/image"
import { Dialog, DialogBackdrop, DialogPanel, TransitionChild } from "@headlessui/react"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"
import { clsx } from "clsx"
import { useState } from "react"
import wideLogoImage from "../images/wide-logo.svg"
import { CartNavLabel } from "../../commerce/components/CartNavLabel"

export interface MobileNavItem {
    name: string
    current?: boolean
    href?: string
    children?: MobileNavItem[]
}

interface Props {
    items: MobileNavItem[]
}

type FlattenedItem = MobileNavItem & { depth: number; group: boolean }

function flattenNavigation(items: MobileNavItem[], depth = 0): FlattenedItem[] {
    return items.flatMap((item) => {
        if (item.children && item.children.length > 0) {
            const groupItem: FlattenedItem = { ...item, depth, group: true }
            const children = flattenNavigation(item.children, depth + 1)
            return [groupItem, ...children]
        }

        return [{ ...item, depth, group: false }]
    })
}

export function MobileFrameClientSide({ items }: Props) {
    const [sidebarOpen, setSidebarOpen] = useState(false)

    const flattened = flattenNavigation(items)
    const label = flattened.find((item) => item.current && !item.group)?.name ?? ""

    return (
        <>
            <Dialog open={sidebarOpen} onClose={setSidebarOpen} className="relative z-10 lg:hidden">
                <DialogBackdrop
                    transition
                    className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
                />

                <div className="fixed inset-0 flex">
                    <DialogPanel
                        transition
                        className="relative mr-16 flex w-full max-w-xs flex-1 transition duration-300 ease-in-out data-[closed]:-translate-x-full"
                    >
                        <TransitionChild>
                            <div className="absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                                <button type="button" onClick={() => setSidebarOpen(false)} className="-m-2.5 p-2.5">
                                    <span className="sr-only">Close sidebar</span>
                                    <XMarkIcon aria-hidden="true" className="size-6 text-white" />
                                </button>
                            </div>
                        </TransitionChild>
                        <div className="bg-primary-600 flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-2">
                            <div className="flex h-16 shrink-0 items-center">
                                <Image className="h-10 w-auto" src={wideLogoImage} priority alt="ResearchWiseAI Logo" />
                            </div>
                            <nav className="flex flex-1 flex-col">
                                <ul className="-mx-2 flex-1 space-y-1">
                                    {flattened.map((item) => (
                                        <li key={item.name}>
                                            {item.group ? (
                                                <div className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-primary-200/70">
                                                    {item.name}
                                                </div>
                                            ) : (
                                                <a
                                                    href={item.href}
                                                    className={clsx(
                                                        item.current
                                                            ? "bg-primary-700 text-white"
                                                            : "text-primary-200 hover:bg-primary-700 hover:text-white",
                                                        "group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold",
                                                        item.depth > 0 ? "pl-4" : undefined
                                                    )}
                                                >
                                                    {item.name}
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                                <div className="-mx-2 mt-auto pt-4">
                                    <a
                                        href="/academy/cart"
                                        className="hover:bg-primary-700 flex items-center gap-x-3 rounded-md px-2 py-2 text-sm/6 font-semibold text-primary-100"
                                    >
                                        <CartNavLabel className="flex-1 text-left" />
                                    </a>
                                </div>
                            </nav>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>
            <div className="bg-primary-600 sticky top-0 flex items-center gap-x-6 px-4 py-4 shadow-sm sm:px-6 lg:hidden">
                <button
                    type="button"
                    onClick={() => setSidebarOpen(true)}
                    className="text-primary-200 -m-2.5 p-2.5 lg:hidden"
                >
                    <span className="sr-only">Open sidebar</span>
                    <Bars3Icon aria-hidden="true" className="size-6" />
                </button>
                <div className="flex-1 text-sm/6 font-semibold text-white">{label}</div>
            </div>
        </>
    )
}
